import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "components/MDButton";

const fields = [
  { key: "company_id", name: "Company Id", category: "CampaignCompanies" },
  {
    key: "external_row_id",
    name: "External Row Id",
    category: "CampaignCompanies",
  },
  { key: "company_name", name: "Company Name", category: "CampaignCompanies" },
  {
    key: "company_phone",
    name: "Company Phone",
    category: "CampaignCompanies",
  },
  {
    key: "company_email",
    name: "Company Email",
    category: "CampaignCompanies",
  },
  { key: "company_url", name: "Company Url", category: "CampaignCompanies" },
  {
    key: "primary_street_address1",
    name: "Primary Street Address 1",
    category: "CampaignCompanies",
  },
  {
    key: "primary_street_address2",
    name: "Primary Street Address 2",
    category: "CampaignCompanies",
  },
  {
    key: "primary_state",
    name: "Primary State",
    category: "CampaignCompanies",
  },
  { key: "primary_city", name: "Primary City", category: "CampaignCompanies" },
  {
    key: "primary_postal_code",
    name: "Primary Postal Code",
    category: "CampaignCompanies",
  },
  {
    key: "primary_country",
    name: "Primary Country",
    category: "CampaignCompanies",
  },
  {
    key: "company_category",
    name: "Company Category",
    category: "CampaignCompanies",
  },
  { key: "latitude", name: "Latitude", category: "CampaignCompanies" },
  { key: "longitude", name: "Longitude", category: "CampaignCompanies" },
  {
    key: "value_id",
    name: "Value Id",
    category: "CampaignCollateralAttributeValues",
  },
  {
    key: "attribute_id",
    name: "Attribute Id",
    category: "CampaignCollateralAttributeValues",
  },
  {
    key: "campaign_id",
    name: "Campaign Id",
    category: "CampaignCollateralAttributeValues",
  },
  {
    key: "value",
    name: "Value",
    category: "CampaignCollateralAttributeValues",
  },
  {
    key: "attribute_id",
    name: "Attribute Id",
    category: "CampaignCollateralAttributes",
  },
  {
    key: "attribute_name",
    name: "Attribute Name",
    category: "CampaignCollateralAttributes",
  },
  {
    key: "field_type_id",
    name: "Field Type Id",
    category: "CampaignCollateralAttributes",
  },
  {
    key: "description",
    name: "Description",
    category: "CampaignCollateralAttributes",
  },
  { key: "contact_id", name: "Contact Id", category: "CampaignContacts" },
  {
    key: "contact_type_id",
    name: "Contact Type Id",
    category: "CampaignContacts",
  },
  { key: "first_name", name: "First Name", category: "CampaignContacts" },
  { key: "last_name", name: "Last Name", category: "CampaignContacts" },
  { key: "position", name: "Position", category: "CampaignContacts" },
  { key: "linkedin", name: "LinkedIn", category: "CampaignContacts" },
  { key: "batch_id", name: "Batch Id", category: "CampaignContacts" },
  { key: "email", name: "Email", category: "CampaignContacts" },
  { key: "phone", name: "Phone", category: "CampaignContacts" },
  { key: "department", name: "Department", category: "CampaignContacts" },
  { key: "confidence", name: "Confidence", category: "CampaignContacts" },
  {
    key: "status_type_id",
    name: "Status Type Id",
    category: "CampaignContacts",
  },
];

const conditions = [
  { key: "=", name: "Equals" },
  { key: "!=", name: "Not Equals" },
  { key: ">", name: "Greater Than" },
  { key: "<", name: "Less Than" },
  { key: "LIKE", name: "Include" },
];

const FilterForm = ({ data, setFilterState }) => {
  const [filters, setFilters] = useState([
    { field: "", condition: "", value: "", category: null },
  ]);

  useEffect(() => {
    if (data && filters.every((filter) => !filter.field && !filter.condition)) {
      const flattenedFilters = Object.entries(data).flatMap(
        ([category, categoryData]) =>
          categoryData.filters.map((filter) => ({ ...filter, category }))
      );
      setFilters(
        flattenedFilters.length > 0
          ? flattenedFilters
          : [{ field: "", condition: "", value: "", category: null }]
      );
    }
  }, [data]);

  const formatFilters = useCallback((filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.category) {
        if (!acc[filter.category]) {
          acc[filter.category] = { filters: [] };
        }
        acc[filter.category].filters.push({
          field: filter.field,
          value: filter.value,
          condition: filter.condition,
        });
      }
      return acc;
    }, {});
  }, []);

  useEffect(() => {
    const formattedFilters = formatFilters(filters);
    setFilterState(formattedFilters);
  }, [filters, formatFilters, setFilterState]);

  const handleFilterChange = useCallback(
    (index, field, value) => {
      setFilters((prevFilters) => {
        const updatedFilters = [...prevFilters];
        updatedFilters[index][field] = value;

        if (field === "field") {
          const selectedField = fields.find((f) => f.key === value);
          updatedFilters[index]["category"] = selectedField
            ? selectedField.category
            : null;
        }

        return updatedFilters;
      });
    },
    [setFilters]
  );

  const addFilters = useCallback(() => {
    setFilters((prevFilters) => [
      ...prevFilters,
      { field: "", condition: "", value: "", category: null },
    ]);
  }, []);

  const removeFilter = useCallback((index) => {
    setFilters((prevFilters) => {
      const updatedFilters = [...prevFilters];
      updatedFilters.splice(index, 1);
      return updatedFilters;
    });
  }, []);

  const renderFilterRow = useCallback(
    (filter, index) => (
      <Grid
        container
        spacing={2}
        key={index}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        {/* Field */}
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Field</InputLabel>
            <Select
              label="Field"
              value={filter.field}
              onChange={(e) =>
                handleFilterChange(index, "field", e.target.value)
              }
              fullWidth
              sx={{ height: "45px" }}
            >
              {fields.map((f) => (
                <MenuItem key={f.key} value={f.key}>
                  {f.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Condition */}
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Condition</InputLabel>
            <Select
              label="Condition"
              value={filter.condition}
              onChange={(e) =>
                handleFilterChange(index, "condition", e.target.value)
              }
              fullWidth
              sx={{ height: "45px" }}
            >
              {conditions.map((c) => (
                <MenuItem key={c.key} value={c.key}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Value */}
        <Grid item xs={12} sm={4}>
          <TextField
            label="Value"
            value={filter.value}
            onChange={(e) => handleFilterChange(index, "value", e.target.value)}
            fullWidth
          />
        </Grid>

        {/* Delete Button */}
        <Grid item xs={12} sm={2}>
          <IconButton onClick={() => removeFilter(index)} color="error">
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    ),
    [handleFilterChange, removeFilter, filters]
  );

  return (
    <Box sx={{ pt: 2, pb: 1, gap: 2 }}>
      <Typography variant="h6">Filters</Typography>
      {filters.map(renderFilterRow)}
      <MDButton variant="contained" onClick={addFilters}>
        Add More Fields
      </MDButton>
    </Box>
  );
};

export default FilterForm;
